import React, { useState, useEffect } from 'react';
import './AutoFillProgress.css';

const AutoFillProgress = ({ 
  isVisible, 
  percentage, 
  totalFields, 
  filledFields, 
  onAnimationComplete 
}) => {
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible && percentage > 0) {
      setIsAnimating(true);
      
      // Animate the percentage counter
      const duration = 2000; // 2 seconds
      const increment = percentage / (duration / 16); // 60 FPS
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= percentage) {
          current = percentage;
          clearInterval(timer);
          setTimeout(() => {
            setIsAnimating(false);
            if (onAnimationComplete) {
              onAnimationComplete();
            }
          }, 1000);
        }
        setCurrentPercentage(Math.round(current));
      }, 16);

      return () => clearInterval(timer);
    }
  }, [isVisible, percentage, onAnimationComplete]);

  if (!isVisible) return null;

  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (currentPercentage / 100) * circumference;

  return (
    <div className={`autofill-overlay ${isAnimating ? 'visible' : 'fade-out'}`}>
      <div className="autofill-container">
        <div className="autofill-content">
          {/* Animated background circles */}
          <div className="background-circles">
            <div className="circle circle-1"></div>
            <div className="circle circle-2"></div>
            <div className="circle circle-3"></div>
          </div>

          {/* Main progress circle */}
          <div className="progress-circle-container">
            <svg className="progress-circle" width="120" height="120">
              {/* Background circle */}
              <circle
                cx="60"
                cy="60"
                r="45"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="8"
              />
              {/* Progress circle */}
              <circle
                cx="60"
                cy="60"
                r="45"
                fill="none"
                stroke="url(#gradient)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="progress-stroke"
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4CAF50" />
                  <stop offset="100%" stopColor="#8BC34A" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Percentage text */}
            <div className="percentage-text">
              <span className="percentage-number">{currentPercentage}</span>
              <span className="percentage-symbol">%</span>
            </div>
          </div>

          {/* Info text */}
          <div className="autofill-info">
            <h3 className="autofill-title">
              <span className="sparkle">✨</span>
              Auto-Fill Complete!
              <span className="sparkle">✨</span>
            </h3>
            <p className="autofill-subtitle">
              {filledFields} out of {totalFields} fields populated from your Wallet
            </p>
            
            {/* Success badges */}
            <div className="success-badges">
              {Array.from({ length: filledFields }, (_, index) => (
                <div 
                  key={index} 
                  className="success-badge"
                  style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                >
                  ✅
                </div>
              ))}
            </div>
          </div>

          {/* Floating particles */}
          <div className="particles">
            {Array.from({ length: 12 }, (_, index) => (
              <div 
                key={index}
                className="particle"
                style={{
                  '--delay': `${index * 0.2}s`,
                  '--angle': `${index * 30}deg`
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoFillProgress; 
